import { OperationVariables } from '@apollo/client';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCaretDown,
  faCaretRight,
  faCaretUp,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  IconButton,
  Collapse,
  Paper,
  Skeleton,
  type TableProps as MuiTableProps,
  styled,
} from '@mui/material';
import {
  type Row,
  type SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getExpandedRowModel,
  Header,
  ColumnDefTemplate,
  CellContext,
} from '@tanstack/react-table';
import { concat, join, map } from 'lodash';
import {
  type ReactNode,
  useCallback,
  useMemo,
  useState,
  Fragment,
} from 'react';

import { TBreakpoints, TablePropsBase } from './table-types';

// This type is for casting due to original ColumnMeta interface issue.
interface TableColumnMeta<TData, TValue> {
  className?: string | string[];
  minimumBreakpoint?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  editor?: ColumnDefTemplate<CellContext<TData, TValue>>;
}

const StripedTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(even)': {
    background: `linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1))`,
    backdropFilter: 'brightness(0.95)',
  },
})) as any;

const StyledTableCell = styled(TableCell)(() => ({
  color: 'inherit',
})) as any;

export interface TableProps<TData, TQuery, TVars extends OperationVariables>
  extends TablePropsBase<TData, TQuery, TVars> {
  rows: TData[];
  onSelect?: (row: TData) => void;
  props?: Partial<MuiTableProps>;
  striped?: boolean;
  loading?: boolean;
  loadingRows?: number;
  rowAlignment?: 'top' | 'middle' | 'bottom';
}

export function Table<
  TData,
  TQuery = object,
  TVars extends OperationVariables = object,
>({
  columns,
  renderSubComponent,
  rows,
  onSelect,
  props,
  rowAlignment = 'middle',
  isEditModeEnabled = false,
  onRowEdit = () => {},
  striped = true,
  loading = false,
  loadingRows = 10,
}: TableProps<TData, TQuery, TVars>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const getBreakpointSx = useCallback((breakpoint?: TBreakpoints) => {
    if (!breakpoint) return {};

    return {
      display: { xs: 'none', [breakpoint]: 'table-cell' },
      '@media print': {
        display: 'table-cell',
      },
    };
  }, []);

  const _columns = useMemo(() => {
    const cols = map(
      columns,
      (
        {
          label,
          field,
          formatter,
          minSize,
          maxSize,
          footer,
          className,
          minimumBreakpoint,
          inlineEditor,
        },
        id,
      ) => {
        const columnHelper = createColumnHelper<TData>();

        const header = () => (
          <div className={join(className || [], ' ')}>{label}</div>
        );

        if (field) {
          return columnHelper.accessor(field, {
            header,
            minSize,
            maxSize,
            id: field.toString(),
            meta: {
              className,
              minimumBreakpoint,
              editor: !!inlineEditor
                ? (props: any) => {
                    if (inlineEditor) {
                      const initialValue = props.cell.getValue() as string;

                      return (
                        <div className={join(className || [], ' ')}>
                          {inlineEditor({
                            initialValue: initialValue,
                            row: props.row,
                            column: props.column,
                            updateRow: onRowEdit,
                          })}
                        </div>
                      );
                    }
                  }
                : undefined,
            },
            cell: (props) => {
              return (
                <div className={join(className || [], ' ')}>
                  {formatter
                    ? formatter(props.row.original, props.row.index + 1)
                    : (props.renderValue() as ReactNode)}
                </div>
              );
            },
            footer: () => (
              <div className={join(className || [], ' ')}>
                {footer?.formatter
                  ? footer.formatter(footer.aggregation(rows, field))
                  : footer?.aggregation(rows, field)}
              </div>
            ),
          });
        }

        return columnHelper.display({
          minSize,
          maxSize,
          header,
          id: id.toString(),
          cell: (props) => {
            const val = formatter
              ? formatter(props.row.original, props.row.index + 1)
              : props.getValue();

            return (
              <div className={join(className || [], ' ')}>
                {val as ReactNode}
              </div>
            );
          },
        });
      },
    );

    if (renderSubComponent) {
      return concat(
        [
          {
            id: 'expander',
            header: () => null,
            cell: ({ row }: { row: Row<TData> }) => {
              const isExpanded = row.getIsExpanded();
              return row.getCanExpand() ? (
                <IconButton
                  onClick={row.getToggleExpandedHandler()}
                  size="small"
                  sx={{ width: 32, height: 32 }}
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faCaretDown : faCaretRight}
                    color={isExpanded ? 'primary' : 'inherit'}
                  />
                </IconButton>
              ) : null;
            },
          },
        ],
        cols,
      );
    }

    return cols;
  }, [columns, onRowEdit, renderSubComponent, rows]);

  const table = useReactTable<TData>({
    data: rows,
    columns: _columns,
    state: { sorting },
    enableColumnFilters: true,
    enableSorting: true,
    getRowCanExpand: () => !!renderSubComponent,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // debugTable: true,
  });

  const renderHeader = useCallback((header: Header<TData, unknown>) => {
    if (!header.isPlaceholder) {
      let icon: IconProp | undefined;

      switch (header.column.getIsSorted()) {
        case 'asc':
          icon = faCaretUp;
          break;
        case 'desc':
          icon = faCaretDown;
          break;
      }

      return (
        <div
          className="d-flex gap-2 text-nowrap"
          style={
            header.column.getCanSort()
              ? { cursor: 'pointer', userSelect: 'none' }
              : {}
          }
          onClick={header.column.getToggleSortingHandler()}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
          {icon && <FontAwesomeIcon icon={icon} size="lg" />}
        </div>
      );
    }
  }, []);

  const TableRowComponent = (striped ? StripedTableRow : TableRow) as any;

  const renderLoadingRows = useCallback(() => {
    const headerGroups = table.getHeaderGroups();
    const columnCount = headerGroups[0]?.headers.length || 0;

    return Array.from({ length: loadingRows }, (_, index) => (
      <TableRowComponent key={`loading-${index}`} hover>
        {Array.from({ length: columnCount }, (_, cellIndex) => {
          const header = headerGroups[0]?.headers[cellIndex];
          const meta = header?.column.columnDef.meta as TableColumnMeta<
            TData,
            unknown
          >;

          return (
            <StyledTableCell
              key={`loading-cell-${index}-${cellIndex}`}
              sx={getBreakpointSx(meta?.minimumBreakpoint)}
            >
              <Skeleton variant="text" height={40} sx={{ m: -0.5 }} />
            </StyledTableCell>
          );
        })}
      </TableRowComponent>
    ));
  }, [table, loadingRows, getBreakpointSx, TableRowComponent]);

  const displayFooter = useMemo(() => {
    return columns.some((col) => col.footer);
  }, [columns]);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ backgroundColor: 'inherit', color: 'inherit' }}
    >
      <MuiTable
        size="small"
        {...props}
        // sx={{ backgroundColor: 'inherit', ...props?.sx }}
      >
        <TableHead>
          {map(table.getHeaderGroups(), (headerGroup) => (
            <TableRow key={headerGroup.id}>
              {map(headerGroup.headers, (header) => {
                const meta = header.column.columnDef.meta as TableColumnMeta<
                  TData,
                  unknown
                >;
                return (
                  <StyledTableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      ...getBreakpointSx(meta?.minimumBreakpoint),
                    }}
                  >
                    {renderHeader(header)}
                  </StyledTableCell>
                );
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {loading
            ? renderLoadingRows()
            : table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <TableRowComponent
                    hover
                    sx={{
                      verticalAlign: rowAlignment,
                      backgroundColor: row.getIsExpanded()
                        ? 'action.selected'
                        : 'transparent',
                      cursor: onSelect ? 'pointer' : 'default',
                    }}
                    data-id={buildRowDataId(row)}
                    onClick={() => onSelect && onSelect(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef
                        .meta as TableColumnMeta<TData, unknown>;
                      return (
                        <StyledTableCell
                          key={`${cell.id}-${cell.getValue()}`}
                          sx={getBreakpointSx(meta?.minimumBreakpoint)}
                        >
                          {isEditModeEnabled &&
                            !!meta?.editor &&
                            flexRender(meta.editor, cell.getContext())}
                          {isEditModeEnabled &&
                            !meta?.editor &&
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          {!isEditModeEnabled &&
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                        </StyledTableCell>
                      );
                    })}
                  </TableRowComponent>
                  {renderSubComponent && (
                    <TableRow>
                      <StyledTableCell
                        colSpan={row.getVisibleCells().length}
                        sx={{
                          backgroundColor: 'action.selected',
                          borderBottom: row.getIsExpanded()
                            ? 'none'
                            : '1px solid rgba(224, 224, 224, 1)',
                        }}
                      >
                        <Collapse in={row.getIsExpanded()}>
                          {renderSubComponent(row)}
                        </Collapse>
                      </StyledTableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
        </TableBody>
        {displayFooter && (
          <TableFooter>
            {table.getFooterGroups().map((footerGroup) => (
              <TableRow key={footerGroup.id}>
                {footerGroup.headers.map((footer) => {
                  const meta = footer.column.columnDef.meta as TableColumnMeta<
                    TData,
                    unknown
                  >;
                  return (
                    <StyledTableCell
                      key={footer.id}
                      sx={{
                        fontWeight: 'bold',
                        ...getBreakpointSx(meta?.minimumBreakpoint),
                      }}
                    >
                      {footer.isPlaceholder
                        ? null
                        : flexRender(
                            footer.column.columnDef.footer,
                            footer.getContext(),
                          )}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableFooter>
        )}
      </MuiTable>
    </TableContainer>
  );
}
function buildRowDataId<TData>(row: Row<TData>) {
  // @ts-ignore Typescript has issue with this
  return row.original._id ?? '';
}
